import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Avatar, Button, EmptyState, ListRow, Sheet } from "@qota/ui";
import { getFriends } from "../lib/friends";
import { listGroups } from "../lib/groups";
import { queryKeys } from "../lib/query-keys";

/**
 * Il più della barra in basso porta **sempre** alla stessa cosa: una spesa
 * nuova. Questo hook decide solo dove.
 *
 * Dentro un gruppo non c'è niente da chiedere. Fuori, la domanda è una sola —
 * in quale gruppo — e la risposta dipende da quanti ce ne sono:
 *
 * - **uno solo**: si va dritti. Un foglio con una voce sola è un tocco
 *   sprecato per chiedere una cosa già decisa;
 * - **più di uno**: si sceglie;
 * - **nessuno**: non si può registrare una spesa senza un gruppo, e invece di
 *   un vicolo cieco si offre di crearne uno.
 *
 * Le destinazioni sono i gruppi **e le coppie**: dopo aver aggiunto un amico
 * deve esserci un posto dove mettere la spesa con lui, altrimenti quel legame
 * non serve a niente. Le coppie non compaiono fra i gruppi, quindi arrivano
 * dall'elenco degli amici.
 *
 * Entrambe le liste sono già in cache quando si arriva dalle schermate Gruppi o
 * Amici, quindi nel caso normale la scelta non costa nessuna attesa.
 */
export function useNewExpense(): { open: () => void; sheet: React.ReactNode } {
  const navigate = useNavigate();
  const [asking, setAsking] = useState(false);

  const { data: groups } = useQuery({ queryKey: queryKeys.groups, queryFn: listGroups });
  const { data: friends } = useQuery({ queryKey: queryKeys.friends, queryFn: getFriends });

  const destinations: Destination[] = [
    ...(groups ?? []).map((group) => ({
      id: group.id,
      name: group.name,
      note: `${group.memberCount} ${group.memberCount === 1 ? "persona" : "persone"}`,
      isPair: false,
    })),
    ...(friends?.friends ?? []).flatMap((friend) =>
      friend.groups
        .filter((entry) => entry.kind === "pair")
        .map((entry) => ({
          id: entry.groupId,
          name: friend.displayName,
          note: "solo voi due",
          isPair: true,
        })),
    ),
  ];

  function go(groupId: string) {
    setAsking(false);
    void navigate({ to: "/gruppi/$groupId/spese/nuova", params: { groupId } });
  }

  return {
    open: () => {
      if (destinations.length === 1) {
        go(destinations[0]!.id);
        return;
      }

      setAsking(true);
    },
    sheet: (
      <PickGroupSheet
        destinations={destinations}
        open={asking}
        onClose={() => setAsking(false)}
        onPick={go}
        onCreateGroup={() => {
          setAsking(false);
          void navigate({ to: "/gruppi/nuovo" });
        }}
      />
    ),
  };
}

interface Destination {
  id: string;
  name: string;
  note: string;
  isPair: boolean;
}

function PickGroupSheet({
  destinations,
  open,
  onClose,
  onPick,
  onCreateGroup,
}: {
  destinations: Destination[];
  open: boolean;
  onClose: () => void;
  onPick: (groupId: string) => void;
  onCreateGroup: () => void;
}) {
  const empty = destinations.length === 0;

  return (
    <Sheet
      open={open}
      title={empty ? "Serve prima un gruppo" : "Dove la metti?"}
      subtitle={empty ? undefined : "La spesa verrà aggiunta qui"}
      size={empty ? "auto" : "tall"}
      onClose={onClose}
      footer={
        empty ? (
          <Button variant="accent" size="lg" full onClick={onCreateGroup}>
            Crea un gruppo
          </Button>
        ) : (
          <Button variant="secondary" size="lg" full onClick={onClose}>
            Annulla
          </Button>
        )
      }
    >
      {empty ? (
        <EmptyState
          icon="users"
          title="Non hai ancora gruppi"
          body="Una spesa si divide fra persone: crea un gruppo, mettici dentro chi vuoi, e potrai segnare la prima spesa."
        />
      ) : (
        destinations.map((destination) => (
          <ListRow
            key={destination.id}
            leading={<Avatar name={destination.name} size={32} />}
            title={destination.name}
            subtitle={destination.note}
            onClick={() => onPick(destination.id)}
          />
        ))
      )}
    </Sheet>
  );
}
